// Task #2569 — Primitiva lifecycle condivisa per la robustezza WSS lato browser.
//
// Unico observer del ciclo di vita del browser (online/offline + ritorno in
// primo piano via visibilitychange/pageshow/focus) a cui i gestori di
// connessione (softphone SIP, Socket.IO tenant + master) si sottoscrivono.
// Niente listener sparsi duplicati: una sola registrazione di event listener
// sul `window`/`document`, fan-out tipizzato ai subscriber.
//
// Questa primitiva NON gestisce auth: si limita a notificare le transizioni di
// lifecycle. Il motore `session-idle` la usa come trigger di RICONTROLLO della
// deadline di inattività al ritorno in primo piano (non come attività utente).

export type LifecycleEvent =
  | { readonly type: 'foreground' }
  | { readonly type: 'online' }
  | { readonly type: 'offline' };

export type LifecycleListener = (event: LifecycleEvent) => void;

export interface BrowserLifecycle {
  /** Sottoscrive le transizioni di lifecycle. Ritorna la funzione di unsubscribe. */
  subscribe(listener: LifecycleListener): () => void;
  /** True se la tab e` attualmente visibile (document.visibilityState === 'visible'). */
  isVisible(): boolean;
  /** True se il browser si considera online (navigator.onLine). */
  isOnline(): boolean;
}

const FOREGROUND_DEBOUNCE_MS = 250;

class BrowserLifecycleImpl implements BrowserLifecycle {
  private readonly listeners = new Set<LifecycleListener>();
  private installed = false;
  private foregroundTimer: ReturnType<typeof setTimeout> | null = null;

  public subscribe(listener: LifecycleListener): () => void {
    this.listeners.add(listener);
    this.ensureInstalled();
    return () => {
      this.listeners.delete(listener);
    };
  }

  public isVisible(): boolean {
    if (typeof document === 'undefined') return true;
    return document.visibilityState === 'visible';
  }

  public isOnline(): boolean {
    if (typeof navigator === 'undefined') return true;
    return navigator.onLine;
  }

  private ensureInstalled(): void {
    if (this.installed || typeof window === 'undefined') return;
    this.installed = true;

    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
    window.addEventListener('focus', this.handleForegroundCandidate);
    window.addEventListener('pageshow', this.handleForegroundCandidate);
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }
  }

  private readonly handleOnline = (): void => {
    this.emit({ type: 'online' });
    // Tornare online equivale a un ritorno-in-primo-piano per i consumer:
    // devono ri-validare/ri-connettere subito.
    this.scheduleForeground();
  };

  private readonly handleOffline = (): void => {
    this.emit({ type: 'offline' });
  };

  private readonly handleVisibilityChange = (): void => {
    if (this.isVisible()) {
      this.scheduleForeground();
    }
  };

  private readonly handleForegroundCandidate = (): void => {
    if (this.isVisible()) {
      this.scheduleForeground();
    }
  };

  /**
   * Debounce: visibilitychange + focus + pageshow spesso scattano insieme al
   * risveglio; coalizziamo in un singolo evento 'foreground'.
   */
  private scheduleForeground(): void {
    if (this.foregroundTimer !== null) {
      clearTimeout(this.foregroundTimer);
    }
    this.foregroundTimer = setTimeout(() => {
      this.foregroundTimer = null;
      this.emit({ type: 'foreground' });
    }, FOREGROUND_DEBOUNCE_MS);
  }

  private emit(event: LifecycleEvent): void {
    this.listeners.forEach((listener) => {
      try {
        listener(event);
      } catch {
        // Un subscriber che lancia non deve impedire il fan-out agli altri.
      }
    });
  }
}

/** Singleton condiviso da tutti i gestori di connessione (tenant + master). */
export const browserLifecycle: BrowserLifecycle = new BrowserLifecycleImpl();

// ---------------------------------------------------------------------------
// Backoff esponenziale con jitter de-correlato.
//
// Algoritmo "decorrelated jitter" (AWS Architecture Blog): ad ogni tentativo
// il delay e` random tra `baseMs` e `prev * 3`, troncato a `maxMs`. Evita il
// thundering-herd di piu` client che si risvegliano insieme dopo un outage.
// ---------------------------------------------------------------------------

export interface DecorrelatedJitterBackoff {
  /** Calcola e ritorna il prossimo delay (ms), avanzando lo stato interno. */
  next(): number;
  /** Riporta il backoff al valore base (dopo una connessione riuscita). */
  reset(): void;
}

export interface DecorrelatedJitterOptions {
  /** Delay minimo / iniziale in ms. */
  readonly baseMs: number;
  /** Cap massimo del delay in ms. */
  readonly maxMs: number;
}

export function createDecorrelatedJitter(
  options: DecorrelatedJitterOptions,
): DecorrelatedJitterBackoff {
  const { baseMs, maxMs } = options;
  let prev = baseMs;
  return {
    next(): number {
      const upper = Math.min(maxMs, prev * 3);
      const delay = baseMs + Math.random() * (upper - baseMs);
      prev = Math.min(maxMs, Math.max(baseMs, delay));
      return Math.round(prev);
    },
    reset(): void {
      prev = baseMs;
    },
  };
}
